import React from "react";
import useBackend from "../../../hooks/useBackend.js";
import useBackendAction from "../../../hooks/useBackendAction.js";
import DataGridPresetCalls from "../../../../config/calls/DataGridPresetCalls.js";
import { sortPresetsByName } from "./sortPresetsByName.js";

/**
 * Loads and mutates data grid presets for a gridKey.
 *
 * @param {string|undefined} gridKey
 * @param {boolean} enabled
 */
export function useDataGridPresets(gridKey, enabled) {
  const listCall = useBackend(() => DataGridPresetCalls.list({ gridKey }), [gridKey], enabled && Boolean(gridKey));

  const createAction = useBackendAction((dtoIn) => DataGridPresetCalls.create(dtoIn));
  const renameAction = useBackendAction((dtoIn) => DataGridPresetCalls.rename(dtoIn));
  const setDefaultAction = useBackendAction((dtoIn) => DataGridPresetCalls.setDefault(dtoIn));
  const deleteAction = useBackendAction((dtoIn) => DataGridPresetCalls.delete(dtoIn));

  const presets = React.useMemo(() => sortPresetsByName(listCall.data?.presets ?? []), [listCall.data?.presets]);
  const loaded = !enabled || listCall.feedback === "ready" || listCall.feedback === "error";

  const reloadPresets = React.useCallback(() => {
    if (enabled && gridKey) {
      listCall.fetch();
    }
  }, [enabled, gridKey, listCall]);

  const createPreset = React.useCallback(
    async (dtoIn) => {
      const response = await createAction.fetch(dtoIn);
      reloadPresets();
      return response?.data;
    },
    [createAction, reloadPresets],
  );

  const renamePreset = React.useCallback(
    async (dtoIn) => {
      const result = await renameAction.fetch(dtoIn);
      reloadPresets();
      return result;
    },
    [renameAction, reloadPresets],
  );

  const setDefaultPreset = React.useCallback(
    async (dtoIn) => {
      const result = await setDefaultAction.fetch(dtoIn);
      reloadPresets();
      return result;
    },
    [setDefaultAction, reloadPresets],
  );

  const deletePreset = React.useCallback(
    async (dtoIn) => {
      const result = await deleteAction.fetch(dtoIn);
      reloadPresets();
      return result;
    },
    [deleteAction, reloadPresets],
  );

  return {
    presets,
    loaded,
    reloadPresets,
    createPreset,
    renamePreset,
    setDefaultPreset,
    deletePreset,
    createFeedback: createAction.feedback,
    renameFeedback: renameAction.feedback,
    setDefaultFeedback: setDefaultAction.feedback,
    deleteFeedback: deleteAction.feedback,
  };
}
